import { A } from '@adornis/base/env-info.js';
import { logger } from '@adornis/base/logging.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerMutation } from '@adornis/baseql/metadata/register.js';
import { getByID } from '@adornis/baseql/operations/mongo.js';
import { getCollection } from '@adornis/baseql/server/collections.js';
import { type BaseQLSelectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { isDefined } from '@adornis/buchungsheld-db/util.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { Price } from '@adornis/payments/db/prices.js';
import { AdornisTemplater } from '@adornis/templater/server/index.js';
import { CurrentUserInfo } from '@adornis/users/db/currentUserInfo.js';
import { generateLoginToken } from '@adornis/users/server/token.js';
import { strictEqual } from 'assert';
import relativeTemplaterFilePath from 'file:./assets/SD-ILV-Formular-Template.docx';
import { DateTime } from 'luxon';
import { dirname, join } from 'path';
import { fileURLToPath } from 'url';
import { BHInvoice } from '../../entities/BHInvoice.js';
import { BHPayment } from '../../entities/BHPayment.js';
import { Participation } from '../../entities/Participation.js';
import { Einzelberatung, EinzelberatungSlotConfiguration } from '../../entities/einzelberatung.js';
import {
  getEmailVarsFromEinzelberatung,
  getEmailVarsFromUser,
  getEmailVarsFromWorkshop,
} from '../../entities/mail-helper.js';
import { InstitutionMailTemplate, MailTemplateType, MailTemplateVars } from '../../entities/mail-templates.js';
import { Product } from '../../entities/products.js';
import { UserStammdaten } from '../../entities/stammdaten.js';
import { User } from '../../entities/user.js';
import { Workshop } from '../../entities/workshop.js';
import { ParticipationState } from '../../enums/enums.js';
import { sendInstitutionEmail } from '../../mails/sendInstitutionMail.js';
import { notifyBestaetigungWorkshopRegistrierung } from '../../notification/bestaetigung-workshop-registrierung.js';
import { printComponent } from '../../print-with-user-session.js';
import type { PaymentPlugin } from '../plugin-types.js';
import {
  SDHessenOccupations,
  SDInstitutionEnum,
  SDInvoiceReceiverEnum,
  SDOccupationEnum,
  SDStammdaten,
  type SDInvoiceMailData,
} from '../stammdaten/sd-stammdaten-plugin.js';
import { BHSDOrderNr } from './BHSDOrderNr.js';
import { defaultPaymentPlugin } from './default.js';
import { createPaymentFromParticipations } from './helper.js';

@Entity()
export class SDInvoice extends BHInvoice {
  static override _class = 'SDInvoice';

  // pdf file path of interal-invoice version for accouning
  @Field(type => String)
  pdfInternalID: Maybe<string>;

  @Field(type => String)
  ilvFormularID: Maybe<string>;

  // somtimes receiver should not be the user
  @Field(type => String)
  mailReceiver: Maybe<string>;
}

const isMedMail = (email: string) => email?.endsWith('med.uni-frankfurt.de');
const isAllgemeinMedMail = (email: string) => email?.endsWith('allgemeinmedizin.uni-frankfurt.de');
export const isSDInternMail = (email: string) =>
  !isMedMail(email) && !isAllgemeinMedMail(email) && email?.endsWith('.uni-frankfurt.de');

const isHessen = (occupation: SDOccupationEnum) => SDHessenOccupations.includes(occupation);

const getProductPriceLogic = (
  product: NarrowedEntity<
    Product,
    { institutionID: 1; prices: { _id: 1; unitAmount: 1; tax_behavior: 1; currency: 1 } }
  >,
  user: NarrowedEntity<User, { _id: 1; firstName: 1; lastName: 1; title: 1; email: 1 }>,
  stammdaten: NarrowedEntity<SDStammdaten, { occupation: 1; department: 1 }>,
) => {
  const prices = product?.prices;
  if (!prices) return null;

  const email = user.email;
  const occupation = stammdaten.occupation;
  const department = stammdaten.department;

  if (!occupation) throw new Error('occupation not set; cant calculate price');
  if (!department) throw new Error('department not set; cant calculate price');

  if (isSDInternMail(email) || [SDInstitutionEnum.SDMEDIZIN, SDInstitutionEnum.SDUNIKLINIKUM].includes(department))
    return prices[0]; // intern
  if (isHessen(occupation)) return prices[1]; // hessen
  return prices[2]; // extern
};

const getProductPrice = async (productID: string, userID: string) => {
  const product = await getByID(
    Product,
    productID,
  )({
    institutionID: 1,
    prices: { _id: 1, unitAmount: 1, tax_behavior: 1, currency: 1, ...Price.allFields },
  });
  if (!product) throw new Error('could not find product ' + productID);

  const user = await getByID(User, userID)({ _id: 1, firstName: 1, lastName: 1, title: 1, email: 1 });
  if (!user) throw new Error('could not find user ' + userID);

  const stammdaten = await UserStammdaten.getExistingStammdaten<SDStammdaten>(
    user._id,
    product.institutionID,
    null,
  )({ occupation: 1, department: 1 });

  if (!user) throw new Error('user not found ' + userID);
  if (!stammdaten) throw new Error('stammdaten not found user=' + userID + ', institution=' + product.institutionID);

  return getProductPriceLogic(product, user, stammdaten) as Price | null;
};

export function sdTimeSlot(from: DateTime, till: DateTime) {
  return [from, till].map(date => date.toFormat('dd.MM.yyyy HH:mm')).join(' - ');
}

const paymentPluginSdPay = registerMutation({
  type: () => String,
  operationName: 'paymentPluginSdPay',
  params: [
    { type: () => String, name: 'institutionID' },
    { type: () => [Participation], name: 'participationIds' },
  ],
  resolve: (institutionID: string, _participations: NarrowedEntity<Participation, { _id: 1; productID: 1 }>[]) => {
    return async () => {
      const participations = await getByID(
        Participation,
        _participations.map(pa => pa._id),
      )({
        _id: 1,
        userID: 1,
        user: {
          _id: 1,
          email: 1,
          firstName: 1,
          lastName: 1,
          title: 1,
          username: 1,
          isLdapAccount: 1,
          whatsappNotification: 1,
          whatsappPhone: 1,
          suspentions: { _id: 1, institutionID: 1 },
          receivesNewsletter: 1,
        },
        productID: 1,
        product: {
          _id: 1,
          name: 1,
          institutionID: 1,
          hasPrice: 1,
          prices: { unitAmount: 1, currency: 1 },
        },
        createdAt: 1,
        updatedAt: 1,
        state: 1,
        isNoShow: 1,
        signoffDate: 1,
        registrationDate: 1,
        fulfilledParticipationRequirements: 1,
        lastStateChangeWasInitiatedByAdmin: 1,
        currentPayment: {
          status: 1,
          participationIDs: 1,
          orderNr: { year: 1, nr: 1 },
          amount: 1,
          discount: 1,
        },
        productPriceForUser: { unitAmount: 1, currency: 1 },
      });

      const selection = {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
      } as const satisfies BaseQLSelectionSet<User>;
      const user = (await CurrentUserInfo.getMyself<User>()(selection)) as NarrowedEntity<User, typeof selection>;
      if (!user) throw new Error('user should be loggedIn');

      // create Payment
      const stammdaten = await UserStammdaten.getByUserAndInstitution<SDStammdaten>(
        user._id,
        institutionID,
      )(SDStammdaten.allFields);
      if (!stammdaten) throw new Error('could not find stammdaten');
      const isIntern = isSDInternMail(user.email) && stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL;

      const payment = await createPaymentFromParticipations(institutionID, participations, getProductPrice);
      payment.orderNr = await BHSDOrderNr.create(institutionID, isIntern ? 'intern' : 'extern');
      payment.userReference.userID = user._id;

      const paymentID = await payment.create();
      logger.info({ paymentID }, '(sd) payment created');

      // send Mails
      await notifyBestaetigungWorkshopRegistrierung(institutionID, user, participations);

      // create Invoice
      const invoiceID = A.getGloballyUniqueID();
      const invoice = new SDInvoice({ _id: invoiceID, paymentID, userID: user._id, institutionID });

      const createInvoicePdf = async (component: string, filenameType: string, params: any) => {
        const buffer = await printComponent(component, {
          user,
          timeout: 25 * 1000,
          queryParams: { payment: payment._id, ...params },
        });

        return await AdornisFile.createFromBuffer(buffer, `invoice-sd-${filenameType}-${invoiceID}.pdf`);
      };

      await (async () => {
        if (stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL) {
          if (!isSDInternMail(user.email)) throw new Error('invalid');
          const invoiceCustomer = await createInvoicePdf('sd-invoice-customer-formular3', 'customer', {
            invoiceProjectNumber: '64003000',
            visitorAddress: 'house_of_labor',
            invoiceTax: 0,
            invoiceReceiverAddress: 'user',
            invoiceKontierungsstempel: false,
            otherAttachement: 'ilv-formular',
          } satisfies SDInvoiceMailData);

          // ilv-formualr
          const filePath = join(dirname(fileURLToPath(import.meta.url)), relativeTemplaterFilePath);
          const templater = AdornisTemplater.fromFile(filePath);

          templater.render({
            date: payment.createdAt.toFormat('dd.LL.yyyy'),
            orderNr: payment.orderNr.getOrderNr(),
            year: payment.createdAt.year.toString(),
            amountInEuro: (payment.amount / 100).toFixed(2),
          });
          const pdfBuffer = await templater.toPdfBuffer();
          const pdfIlvFile = await AdornisFile.createFromBuffer(pdfBuffer, `invoice-sd-ilv-${invoiceID}.pdf`);

          invoice.pdfID = invoiceCustomer._id;
          invoice.ilvFormularID = pdfIlvFile._id;

          return;
        }

        const isDepartmentMedizin = stammdaten.department === SDInstitutionEnum.SDMEDIZIN;
        const isDepartmentUniklinikum = stammdaten.department === SDInstitutionEnum.SDUNIKLINIKUM;

        if (
          (isDepartmentMedizin || isDepartmentUniklinikum) &&
          stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL
        ) {
          const config = {
            invoiceProjectNumber: '64120700',
            visitorAddress: 'bockenheim',
            invoiceTax: 0,
            invoiceReceiverAddress: isDepartmentMedizin ? 'med' : 'dentMed',
            invoiceKontierungsstempel: false,
            otherAttachement: null,
          } satisfies SDInvoiceMailData;

          const customerForm = isDepartmentMedizin ? 'sd-invoice-customer-formular5' : 'sd-invoice-customer-formular6';
          const invoiceCustomer = await createInvoicePdf(customerForm, 'customer', config);
          const invoiceInternal = await createInvoicePdf(customerForm, 'internal', {
            ...config,
            invoiceKontierungsstempel: true,
          });

          invoice.mailReceiver = 'kowatsch@sd.uni-frankfurt.de';
          invoice.pdfID = invoiceCustomer._id;
          invoice.pdfInternalID = invoiceInternal._id;
          return;
        }

        // @ts-expect-error first check makes this impossible to be true
        if (stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDINTERNAL) throw new Error('invalid');

        if (isSDInternMail(user.email) && stammdaten.invoiceReceiver === SDInvoiceReceiverEnum.SDEXTERNAL)
          throw new Error('invalid');

        const config = {
          invoiceProjectNumber: '64120700',
          visitorAddress: 'house_of_labor',
          invoiceTax: 0,
          invoiceReceiverAddress: 'user',
          invoiceKontierungsstempel: false,
          otherAttachement: null,
        } satisfies SDInvoiceMailData;

        const invoiceCustomer = await createInvoicePdf('sd-invoice-customer-formular1', 'customer', config);
        const invoiceInternal = await createInvoicePdf('sd-invoice-customer-formular1', 'internal', {
          ...config,
          invoiceKontierungsstempel: true,
        });

        invoice.pdfID = invoiceCustomer._id;
        invoice.pdfInternalID = invoiceInternal._id;
        return;
      })();

      await invoice.create();

      return paymentID;
    };
  },
});

export const sdPaymentPlugin: PaymentPlugin<'invoice', 'intern' | 'hessen' | 'extern'> = {
  id: 'payment::sd',
  type: 'payment',
  paymentMethods: ['invoice'],
  getProductPrice,
  createPayment: async (participationIDs: string[], _paymentMethod) => {
    const user = CurrentUserInfo.getMyself()({ _id: 1 });
    if (!user) throw new Error('user not found');

    const participations = await getByID(
      Participation,
      participationIDs,
    )({
      _id: 1,
      userID: 1,
      user: {
        _id: 1,
        email: 1,
        firstName: 1,
        lastName: 1,
        title: 1,
        username: 1,
        isLdapAccount: 1,
        whatsappNotification: 1,
        whatsappPhone: 1,
        suspentions: { _id: 1, institutionID: 1 },
        receivesNewsletter: 1,
      },
      productID: 1,
      product: {
        _id: 1,
        name: 1,
        institutionID: 1,
        hasPrice: 1,
        prices: { unitAmount: 1, currency: 1 },
      },
      createdAt: 1,
      updatedAt: 1,
      state: 1,
      isNoShow: 1,
      signoffDate: 1,
      registrationDate: 1,
      fulfilledParticipationRequirements: 1,
      lastStateChangeWasInitiatedByAdmin: 1,
      currentPayment: {
        status: 1,
        participationIDs: 1,
        orderNr: { year: 1, nr: 1 },
        amount: 1,
        discount: 1,
      },
      productPriceForUser: { unitAmount: 1, currency: 1 },
    });

    const institutionID = participations[0]?.product?.institutionID;
    if (!institutionID) throw new Error('something went wrong');

    const paymentID = await paymentPluginSdPay(institutionID, participations)();
    return [paymentID];
  },
  pay: async (paymentID, _paymentMethod) => {
    // nothing to do here; invoice gets paid manually by user
  },

  afterEnrollment: async participationID => {},

  sendInvoice: async (invoiceID: string): Promise<boolean> => {
    const invoice = await getByID(
      SDInvoice,
      invoiceID,
    )({
      _id: 1,
      paymentID: 1,
      pdfID: 1,
      pdfInternalID: 1,
      ilvFormularID: 1,
      mailReceiver: 1,
    });
    if (!invoice) throw new Error('invoice not found');

    const payment = await getByID(
      BHPayment,
      invoice.paymentID,
    )({
      _id: 1,
      createdAt: 1,
      participationIDs: 1,
      amount: 1,
      status: 1,
      orderNr: { year: 1, nr: 1, type: 1 },
    });
    // NOTE: deactivate invoice for now
    return;
    if (!payment) throw new Error('should not happen');

    const participations = await Promise.all(
      payment.participationIDs.map(id =>
        getByID(
          Participation,
          id,
        )({
          _id: 1,
          userID: 1,
          user: {
            _id: 1,
            email: 1,
            firstName: 1,
            lastName: 1,
            title: 1,
            username: 1,
            isLdapAccount: 1,
            whatsappNotification: 1,
            whatsappPhone: 1,
            suspentions: { _id: 1, institutionID: 1 },
            receivesNewsletter: 1,
          },
          productID: 1,
          product: {
            _id: 1,
            name: 1,
            institutionID: 1,
            hasPrice: 1,
            prices: { unitAmount: 1, currency: 1 },
          },
          createdAt: 1,
          updatedAt: 1,
          state: 1,
          isNoShow: 1,
          signoffDate: 1,
          registrationDate: 1,
          fulfilledParticipationRequirements: 1,
          lastStateChangeWasInitiatedByAdmin: 1,
          currentPayment: {
            status: 1,
            participationIDs: 1,
            orderNr: { year: 1, nr: 1 },
            amount: 1,
            discount: 1,
          },
          productPriceForUser: { unitAmount: 1, currency: 1 },
        }),
      ),
    ).then(ps => ps.filter(isDefined));

    const doNotSendInvoice = participations.every(p => !!p.ikh_doNotSendInvoice);
    if (doNotSendInvoice) return false;

    const p = participations[0];
    if (!p) throw new Error('participations required');

    if (p.state === ParticipationState.WAITING) {
      logger.debug('invoice not send because user is on waiting list');
      return false;
    }

    const user = p.user;
    if (!user) throw new Error('user is missing');

    const product = p.product;
    if (!product) throw new Error('product not found');

    const institutionID = product.institutionID;

    const collection = await getCollection<SDStammdaten>(UserStammdaten._class);
    const stammdaten = await collection.findOne({ userID: user._id, institutionID });
    if (!stammdaten) throw new Error('stammdaten not found');

    const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
      institutionID,
      MailTemplateType.SD_INVOICE_USER,
    )(InstitutionMailTemplate.allFields);

    let emailVars = (() => {
      if (product instanceof Workshop) return getEmailVarsFromWorkshop(product, user);
      else if (product instanceof Einzelberatung) return getEmailVarsFromEinzelberatung(product, user);
      else return getEmailVarsFromUser(user);
    })();

    const token = await generateLoginToken({
      userID: user._id,
      userClass: User._class,
      ttl: 1000 * 60 * 60 * 24 * 7 /* 7 days */,
    });

    emailVars[MailTemplateVars.LOGIN_LINK] = InstitutionMailTemplate.mapLoginLinkVar(token);

    const mailContent = await mailTemplate.buildEmail(emailVars);

    if (!mailContent) throw new Error('could not build email');
    await sendInstitutionEmail({
      email: invoice.mailReceiver ?? user.email,
      subject: mailTemplate.subject,
      content: mailContent,
      institutionID,
      attachmentPaths: [
        A.absoluteUrl(AdornisFile.getServeLink(invoice.pdfID) + '/Rechnung.pdf'),
        ...(invoice.ilvFormularID ? [AdornisFile.getServeLink(invoice.ilvFormularID) + '/ilv.pdf'] : []),
      ],
    });

    // institution mails
    if (invoice.pdfInternalID) {
      const s = p.product?.settings;
      if (!s) {
        logger.warn('no settings found; cant send accounting mails');
        return true;
      }

      const mailTemplate = await InstitutionMailTemplate.getMailTemplateForInstitution(
        institutionID,
        MailTemplateType.SD_INVOICE_INSTITUTION,
      )(InstitutionMailTemplate.allFields);

      let emailVars = (() => {
        if (product instanceof Workshop) return getEmailVarsFromWorkshop(product, user);
        else if (product instanceof Einzelberatung) return getEmailVarsFromEinzelberatung(product, user);
        else return getEmailVarsFromUser(user);
      })();

      const mailContent = await mailTemplate.buildEmail(emailVars);
      if (!mailContent) throw new Error('could not build email');

      const institutionMail1 = s.interAccountingEmail1 ?? s.inheritedInterAccountingEmail1?.[1];
      const institutionMail2 = s.interAccountingEmail2 ?? s.inheritedInterAccountingEmail2?.[1];
      const institutionMail3 = s.interAccountingEmail3 ?? s.inheritedInterAccountingEmail3?.[1];
      const institutionMails = [institutionMail1, institutionMail2, institutionMail3]
        .filter(it => !!it)
        .filter(isDefined);

      if (institutionMails.length === 0) {
        logger.warn('no institution-mail available; cant send institution-invoice-copy');
        return true;
      }
      for (const institutionMail of institutionMails) {
        await sendInstitutionEmail({
          email: institutionMail,
          subject: mailTemplate.subject,
          content: mailContent,
          institutionID,
          attachmentPaths: [A.absoluteUrl(AdornisFile.getServeLink(invoice.pdfInternalID) + '/Rechnung.pdf')],
        });
      }
    }

    payment.status = 'issued';
    await payment.save();

    return true;
  },
  cancleEnrollment: async (participationID: string) => {
    const participation = await getByID(
      Participation,
      participationID,
    )({
      currentPayment: { _id: 1, status: 1 },
      product: { _id: 1, appointments: { start: 1, end: 1 }, slotConfig: EinzelberatungSlotConfiguration.allFields },
    });
    if (!participation?.currentPayment) return;

    const payment = participation.currentPayment;
    switch (payment.status) {
      case 'paid':
        const now = DateTime.now();
        const firstTermin = await (async () => {
          const product = participation.product;
          if (product instanceof Workshop) return product.start;
          if (product instanceof Einzelberatung) return product.slotConfig?.start;
          throw new Error('unreachable');
        })();

        if (!firstTermin || now < firstTermin.minus({ days: 3 })) {
          // free cancelation
          payment.status = 'refund';
          payment.cancelReason = 'signoff';
          await payment.save();
          logger.info({ payment }, 'cancle payment free of charge (sd)');
        } else if (now < firstTermin.plus({ days: 7 })) {
          payment.discount = 40; // pay 60%
          await payment.save();
          logger.info({ payment }, 'cancle payment free of charge (sd)');
        } else void 0; // do nothing (user has to pay full price)
        return;
      default:
        await defaultPaymentPlugin.cancleEnrollment(participationID);
        return;
    }
  },

  priceGroups: ['intern', 'hessen', 'extern'],
};

export function testSDPaymentPlugin() {
  describe('ikh-payment-plugin', () => {
    it('get intern price when user has uni-frankfurt mail', () => {
      const resultPrice = getProductPriceLogic(
        new Product({
          prices: [new Price({ _id: 'intern' }), new Price({ _id: 'hessen' }), new Price({ _id: 'extern' })],
        }),
        new User({
          email: 'foo@bar.uni-frankfurt.de',
        }),
        new SDStammdaten({
          occupation: SDOccupationEnum.SDHIWI_GOETHE,
          department: SDInstitutionEnum.SDGEO,
        }),
      );

      strictEqual(resultPrice?._id, 'intern');
    });

    it('get hessen price when user is teacher', () => {
      const resultPrice = getProductPriceLogic(
        new Product({
          prices: [new Price({ _id: 'intern' }), new Price({ _id: 'hessen' }), new Price({ _id: 'extern' })],
        }),
        new User({
          email: 'foo@bar.de',
        }),
        new SDStammdaten({
          occupation: SDOccupationEnum.SDTEACHER,
          department: SDInstitutionEnum.SDEVANG_THEOLOGIE,
        }),
      );

      strictEqual(resultPrice?._id, 'hessen');
    });

    it('get hessen price when user is hess hochschul angegoriger', () => {
      const resultPrice = getProductPriceLogic(
        new Product({
          prices: [new Price({ _id: 'intern' }), new Price({ _id: 'hessen' }), new Price({ _id: 'extern' })],
        }),
        new User({
          email: 'foo@bar.de',
        }),
        new SDStammdaten({
          occupation: SDOccupationEnum.SDHESS_HOCHSCHULANGEHOERIGE,
          department: SDInstitutionEnum.SDINFORMATIK,
        }),
      );

      strictEqual(resultPrice?._id, 'hessen');
    });
  });
}
